import { useEffect, useRef, useState } from "react";

interface UseCountdownOptions {
  /** Initial countdown value in seconds. Defaults to 300 (5 minutes). */
  totalSeconds?: number;
  /** Start/stop the countdown. When true the timer runs; when false it resets. */
  active?: boolean;
  /** Called when the countdown reaches 0. */
  onExpire?: () => void;
}

interface UseCountdownReturn {
  /** Current remaining seconds, or 0 when inactive/expired. */
  countdown: number;
  /** Whether the countdown is currently running. */
  isActive: boolean;
}

const DEFAULT_TOTAL_SECONDS = 300;

/**
 * A reusable countdown timer that ticks from `totalSeconds` down to 0.
 *
 * The countdown is controlled via the `active` prop:
 * - `active = true`  → starts (or restarts) the countdown from `totalSeconds`
 * - `active = false` → stops the countdown and resets to `totalSeconds`
 * - Countdown reaching 0 also calls `onExpire` and stops automatically.
 *
 * @example
 * // In a component that sends a device command:
 * const { countdown, isActive } = useCountdown({
 *   active: commandStatus === "pending",
 *   onExpire: () => setCommandStatus("failed"),
 * });
 *
 * const handleSend = async () => {
 *   setCommandStatus("pending");   // ← countdown starts automatically
 *   await api.sendCommand(...);
 *   setCommandStatus("success");   // ← countdown stops automatically
 * };
 */
export function useCountdown({
  totalSeconds = DEFAULT_TOTAL_SECONDS,
  active = false,
  onExpire,
}: UseCountdownOptions = {}): UseCountdownReturn {
  const [countdown, setCountdown] = useState(totalSeconds);
  const intervalRef = useRef<number | null>(null);
  const onExpireRef = useRef(onExpire);
  const totalRef = useRef(totalSeconds);

  onExpireRef.current = onExpire;
  totalRef.current = totalSeconds;

  const stopInterval = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!active) {
      stopInterval();
      setCountdown(totalRef.current);
      return;
    }

    // (re)start countdown when active becomes true
    stopInterval();
    setCountdown(totalRef.current);

    intervalRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopInterval();
          onExpireRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return stopInterval;
  }, [active]);

  useEffect(() => () => stopInterval(), []);

  return { countdown, isActive: active && countdown > 0 };
}
